// frontend/src/components/admin/StatsAiTab.jsx
//
// 2026-10-18 — Super admin → Stats AI. Where the model that checks every
// game's transcript after the whistle lives (Gemma on the RTX 5090, through
// the GPU broker on meadowpark2), and whether the after-game speech-to-text
// may borrow one of meadowpark's GTX 1070s through meadowpark's own broker.
//
// Kept in the database, not in .env (per Gilles), and only the super admin
// reaches it (/api/admin/*). Keys are write-only: this page learns whether
// one is saved, never what it is — paste a new one to replace it.
import { useEffect, useState } from 'react';
import { Cpu, Mic, RefreshCw, CheckCircle2, AlertCircle, KeyRound, Save, FlaskConical } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api.js';

const PROVIDER_LABELS = {
  broker: 'GPU broker (Ollama API, with a key)',
  ollama: 'Ollama (no broker)',
  openai: 'OpenAI-compatible server',
  vllm: 'vLLM',
  lmstudio: 'LM Studio',
  llamacpp: 'llama.cpp server',
  none: 'Off — rules only',
};

const fmtTime = (ms) => {
  const n = Number(ms);
  if (!n) return null;
  try { return new Date(n).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }); } catch { return null; }
};

export default function StatsAiTab({ T }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState(null);       // model check result
  const [testing, setTesting] = useState(false);
  const [stt, setStt] = useState(null);         // GPU check result
  const [sttTesting, setSttTesting] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get('/admin/stats-ai');
      setData(d);
      const s = d.settings || {};
      setForm({
        provider: s.provider || 'none', baseUrl: s.baseUrl || '', model: s.model || '', apiKey: '',
        numCtx: s.numCtx ?? '', timeoutS: s.timeoutS ?? '', think: s.think === true ? 'on' : s.think === false ? 'off' : '',
        stt: { enabled: !!s.stt?.enabled, brokerUrl: s.stt?.brokerUrl || d.defaults?.sttBrokerUrl || '', apiKey: '',
               leaseMb: s.stt?.leaseMb ?? d.defaults?.sttLeaseMb ?? 3500, sharedWhisper: s.stt?.sharedWhisper !== false,
               only5090: !!s.stt?.only5090 },
      });
    } catch (e) {
      setData({ error: e.response?.data?.error || 'Could not load the settings' });
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div style={{ fontSize: 12, color: T.text2 }}>Loading…</div>;
  if (data.error) return <div style={{ fontSize: 12, color: T.fire }}>{data.error}</div>;
  if (!form) return null;
  const s = data.settings || {};
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setS = (k, v) => setForm(f => ({ ...f, stt: { ...f.stt, [k]: v } }));

  const body = (extra = {}) => {
    const b = {
      provider: form.provider, baseUrl: form.baseUrl.trim(), model: form.model.trim(),
      numCtx: form.numCtx === '' ? null : Number(form.numCtx),
      timeoutS: form.timeoutS === '' ? null : Number(form.timeoutS),
      think: form.think === 'on' ? true : form.think === 'off' ? false : null,
      stt: { enabled: !!form.stt.enabled, brokerUrl: form.stt.brokerUrl.trim(),
             leaseMb: form.stt.leaseMb === '' ? null : Number(form.stt.leaseMb),
             sharedWhisper: !!form.stt.sharedWhisper, only5090: !!form.stt.only5090 },
      ...extra,
    };
    if (form.apiKey.trim()) b.apiKey = form.apiKey.trim();
    if (form.stt.apiKey.trim()) b.stt.apiKey = form.stt.apiKey.trim();
    return b;
  };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const { data: d } = await api.put('/admin/stats-ai', body(extra));
      toast.success(d.changed?.length ? 'Saved' : 'Nothing changed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save');
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try {
      const b = body();
      const { data: d } = await api.post('/admin/stats-ai/test', {
        provider: b.provider, baseUrl: b.baseUrl, model: b.model, numCtx: b.numCtx, ...(b.apiKey ? { apiKey: b.apiKey } : {}),
      }, { timeout: 40000 });
      setTest(d);
    } catch (e) {
      setTest({ error: e.response?.data?.error || 'The test did not finish' });
    } finally { setTesting(false); }
  };

  const runSttTest = async () => {
    setSttTesting(true); setStt(null);
    try {
      const b = body();
      const { data: d } = await api.post('/admin/stats-ai/test-stt', {
        brokerUrl: b.stt.brokerUrl, leaseMb: b.stt.leaseMb, ...(b.stt.apiKey ? { apiKey: b.stt.apiKey } : {}), tryLease: true,
        sharedWhisper: b.stt.sharedWhisper,
      }, { timeout: 130000 });
      setStt(d);
    } catch (e) {
      setStt({ error: e.response?.data?.error || 'The test did not finish' });
    } finally { setSttTesting(false); }
  };

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, marginBottom: 16 };
  const label = { fontSize: 11, fontWeight: 700, color: T.text2, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 };
  const input = { width: '100%', maxWidth: 460, padding: '7px 10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.s2, color: T.text, fontSize: 13, boxSizing: 'border-box' };
  const btn = { background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 12px', color: T.text2, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 };
  const primary = { ...btn, background: T.ice, color: '#000', fontWeight: 700, border: 'none' };
  const row = { marginBottom: 12 };
  const note = { fontSize: 12, color: T.text2, margin: '4px 0 0', maxWidth: 640 };
  const good = (ok) => (ok ? <CheckCircle2 size={14} color={T.goal} /> : <AlertCircle size={14} color={T.penalty} />);
  const ping = test?.ping;
  const models = Array.isArray(ping?.models) ? ping.models : [];
  const status = s.ready
    ? `Games are checked by ${s.model} after the whistle`
    : s.provider === 'none' ? 'Off — games are worked out by the rules only'
    : s.problem === 'no key' ? `Waiting for Game Day's broker key — until then, rules only`
    : `Not complete (${s.problem}) — rules only`;

  return (
    <div>
      <PressBoxQueueCard T={T} />
      {/* ── the model ─────────────────────────────────────────────── */}
      <div style={card} data-stats-ai>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Cpu size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Stats AI — checks every game after the whistle</h3>
          {/* 2026-10-21 (per Gilles) — the big red switch: cancel every
              running analysis across every team. Stretches already read stay
              cached, so a re-run later only pays for the rest. */}
          <button type="button" data-stop-all
            onClick={async () => {
              if (!window.confirm('Stop EVERY running analysis, on every team?\n\nNothing partial is written; stretches already read stay cached. Re-run any game later from its page.')) return;
              try {
                const { data: r } = await api.post('/admin/stats-ai/stop-all');
                toast.success(r.running ? `Stopping ${r.stopped} of ${r.running} running analyses` : 'Nothing is running right now');
              } catch (e) { toast.error(e.response?.data?.error || 'Could not stop them'); }
            }}
            style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
              background: 'none', border: '1px solid #ff3b3b66', color: '#ff6b6b' }}>
            ■ Stop all analyses
          </button>
        </div>
        <p style={note}>
          The after-game pass hands the transcript to this model, 2½ minutes at a time. If the GPU is busy (the broker's
          training hours), the game gets the rules' result first and the AI check runs again by itself when the card is free.
          If the model cannot be reached, the game keeps the rules' result until someone re-runs it from Press Box.
        </p>
        <p style={{ ...note, color: s.ready ? T.goal : T.puck, fontWeight: 600 }}>{status}{!data.engine ? ' · the stats engine is not turned on on this server' : ''}</p>

        <div style={{ ...row, marginTop: 14 }}>
          <label style={label} htmlFor="sai-provider">Where it runs</label>
          <select id="sai-provider" style={input} value={form.provider} onChange={e => set('provider', e.target.value)}>
            {(data.providers || []).map(p => <option key={p} value={p}>{PROVIDER_LABELS[p] || p}</option>)}
          </select>
        </div>
        {form.provider !== 'none' && <>
          <div style={row}>
            <label style={label} htmlFor="sai-url">Address</label>
            <input id="sai-url" style={input} value={form.baseUrl} placeholder={data.defaults?.baseUrl || ''}
              onChange={e => set('baseUrl', e.target.value)} autoComplete="off" spellCheck={false} />
            {/* The address comes from the server (admin-only): the JS bundle is public, so no address is written into it. */}
            <p style={note}>The GPU broker on meadowpark2 is {data.defaults?.baseUrl ? <><code>{data.defaults.baseUrl}</code> (Tailscale)</> : 'its Tailscale address, port 11435'}. Never Ollama's own port 11434 — going around the broker breaks the clinic's priority.</p>
          </div>
          <div style={row}>
            <label style={label} htmlFor="sai-model">Model</label>
            {/* 2026-10-19 — a dropdown, not typing: the broker's models ≥14B
                (smaller ones aren't up to the stats), gemma first so the
                default family tops the list. Press Test to (re)fill it. */}
            {(() => {
              const sizeOf = (m) => { const x = String(m).match(/(\d+(?:\.\d+)?)b\b/i); return x ? parseFloat(x[1]) : null; };
              const big = models.filter(m => (sizeOf(m) ?? 99) >= 14);
              const sorted = [...big].sort((a, b) => {
                const ga = a.toLowerCase().startsWith('gemma') ? 0 : 1;
                const gb = b.toLowerCase().startsWith('gemma') ? 0 : 1;
                return ga - gb || a.localeCompare(b);
              });
              const opts = sorted.includes(form.model) || !form.model ? sorted : [form.model, ...sorted];
              return (
                <select id="sai-model" style={input} value={form.model} onChange={e => set('model', e.target.value)}>
                  {!form.model && <option value="">— pick a model —</option>}
                  {opts.map(m => <option key={m} value={m}>{m}{m.toLowerCase().startsWith('gemma') ? '' : ''}</option>)}
                </select>
              );
            })()}
            <p style={note}>The broker&rsquo;s models this key may use, 14B and up (press <strong>Test</strong> to refresh the list — gemma first). gemma3:27b and qwen3:32b stay loaded for the clinic; any other model makes the card swap for each game. Pick different ones and re-run a game to compare.</p>
          </div>
          <div style={row}>
            <label style={label} htmlFor="sai-key"><KeyRound size={11} style={{ display: 'inline-block', verticalAlign: '-1px' }} /> Key {form.provider === 'broker' ? '(required)' : '(only if the server wants one)'}</label>
            <input id="sai-key" style={input} type="password" value={form.apiKey} autoComplete="new-password"
              placeholder={s.keySet ? '•••••••• saved — paste a new key to replace it' : "Paste Game Day's key from the broker's /admin"}
              onChange={e => set('apiKey', e.target.value)} />
            {s.keySet && <div style={{ marginTop: 4 }}><button type="button" style={{ ...btn, padding: '2px 8px', fontSize: 11 }} disabled={saving}
              onClick={() => { if (window.confirm('Remove the saved key? The stats AI stops until a new one is saved.')) save({ clearApiKey: true }); }}>Remove saved key</button></div>}
          </div>
          <details style={{ marginBottom: 12 }}>
            <summary style={{ fontSize: 12, color: T.text2, cursor: 'pointer' }}>More settings</summary>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
              <div><label style={label} htmlFor="sai-ctx">Context (tokens)</label>
                <input id="sai-ctx" style={{ ...input, width: 140 }} inputMode="numeric" value={form.numCtx} placeholder="16384" onChange={e => set('numCtx', e.target.value.replace(/[^0-9]/g, ''))} /></div>
              <div><label style={label} htmlFor="sai-to">Time-out (s)</label>
                <input id="sai-to" style={{ ...input, width: 120 }} inputMode="numeric" value={form.timeoutS} placeholder="300" onChange={e => set('timeoutS', e.target.value.replace(/[^0-9]/g, ''))} /></div>
              <div><label style={label} htmlFor="sai-think">Thinking</label>
                <select id="sai-think" style={{ ...input, width: 170 }} value={form.think} onChange={e => set('think', e.target.value)}>
                  <option value="">The model's default</option><option value="on">On</option><option value="off">Off</option>
                </select></div>
            </div>
          </details>
        </>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={primary} disabled={saving} onClick={() => save()}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
          {form.provider !== 'none' && <button type="button" style={btn} disabled={testing} onClick={runTest}><FlaskConical size={13} /> {testing ? 'Testing…' : 'Test'}</button>}
          <button type="button" style={btn} onClick={load}><RefreshCw size={12} /> Reload</button>
        </div>

        {test && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: T.s2, fontSize: 12, color: T.text }} data-stats-ai-test>
            {test.error ? <div style={{ color: T.penalty }}>{test.error}</div>
              : !test.enabled ? <div>Off — nothing to test.</div>
              : ping ? <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>{good(ping.ok)} {ping.ok ? `Answering — ${test.endpoint?.model} may be used` : 'Not usable'}</div>
                {ping.error && <div style={{ color: T.penalty, marginTop: 4 }}>{ping.error}</div>}
                {ping.note && <div style={{ color: T.puck, marginTop: 4 }}>{ping.note}</div>}
                {ping.busy && <div style={{ color: T.puck, marginTop: 4 }}>A training run holds the card right now{fmtTime(ping.busy.until) ? ` (until about ${fmtTime(ping.busy.until)})` : ''}: games get the rules first and the AI check after it ends.</div>}
                {models.length > 0 && <div style={{ color: T.text2, marginTop: 4 }}>This key may use: {models.join(', ')}{Array.isArray(ping.pending) && ping.pending.length ? ` · approved, not on the card yet: ${ping.pending.join(', ')}` : ''}</div>}
                {Array.isArray(ping.loaded) && ping.loaded.length > 0 && <div style={{ color: T.text2, marginTop: 2 }}>Loaded now: {ping.loaded.join(', ')}</div>}
                {ping.broker_version && <div style={{ color: T.text3, marginTop: 2 }}>gpu-broker {ping.broker_version}</div>}
              </> : <div>No answer.</div>}
          </div>
        )}
      </div>

      {/* ── speech-to-text on a meadowpark GPU ─────────────────────── */}
      <div style={card} data-stats-stt>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Mic size={16} color={T.ice} />
          <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Speech-to-text on a meadowpark GPU (GTX 1070)</h3>
        </div>
        <p style={note}>
          The after-game pass can borrow a card from meadowpark's GPU broker (a VRAM lease) to run Whisper large-v3, with the two
          Parakeet models on the CPU. The card goes back after each game, and at once if the broker asks for it. With no card
          free, or this off, the CPU does it as before. Live games always use the CPU.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text, margin: '12px 0', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.stt.enabled} onChange={e => setS('enabled', e.target.checked)} />
          Use a meadowpark GPU for the after-game speech-to-text when one is free
        </label>
        <div style={row}>
          <label style={label} htmlFor="stt-url">meadowpark broker address</label>
          <input id="stt-url" style={input} value={form.stt.brokerUrl} placeholder="http://host.docker.internal:11435"
            onChange={e => setS('brokerUrl', e.target.value)} autoComplete="off" spellCheck={false} />
          <p style={note}>From the engine's container on meadowpark the broker is on the host: <code>http://host.docker.internal:11435</code>.</p>
        </div>
        <div style={row}>
          <label style={label} htmlFor="stt-key"><KeyRound size={11} style={{ display: 'inline-block', verticalAlign: '-1px' }} /> Key on meadowpark's broker</label>
          <input id="stt-key" style={input} type="password" value={form.stt.apiKey} autoComplete="new-password"
            placeholder={s.stt?.keySet ? '•••••••• saved — paste a new key to replace it' : 'The key of a broker application with "May reserve VRAM" ticked'}
            onChange={e => setS('apiKey', e.target.value)} />
          {s.stt?.keySet && <div style={{ marginTop: 4 }}><button type="button" style={{ ...btn, padding: '2px 8px', fontSize: 11 }} disabled={saving}
            onClick={() => { if (window.confirm('Remove the saved key? Speech-to-text goes back to the CPU.')) save({ stt: { ...body().stt, clearApiKey: true } }); }}>Remove saved key</button></div>}
        </div>
        <div style={row}>
          <label style={label} htmlFor="stt-mb">GPU memory to borrow (MB)</label>
          <input id="stt-mb" style={{ ...input, width: 140 }} inputMode="numeric" value={form.stt.leaseMb}
            onChange={e => setS('leaseMb', e.target.value.replace(/[^0-9]/g, ''))} />
          <p style={note}>The peak, asked for once per game. Whisper large-v3 in int8 needs about 3 GB on a 1070; the application's limit in the broker must be at least this.</p>
        </div>
        {/* 2026-10-24 (per Gilles) — the 5090's shared Whisper, when it is idle */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: T.text, margin: '4px 0 12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.stt.sharedWhisper} onChange={e => setS('sharedWhisper', e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            Use the RTX 5090's shared Whisper (meadowpark2) for each piece while that card is idle — much faster; the 1070 takes every other piece
            <span style={{ display: 'block', fontSize: 12, color: T.text2, marginTop: 2 }}>
              Uses the stats model's broker address and Press Box key above. Before every piece the engine checks the 5090: no training,
              no text-model job running or waiting, no transcript running or waiting, and "May transcribe" ticked for press_box. The clinic's
              dictations always go first. Needs gpu-broker 2.7.1 with its shared Whisper on meadowpark2; until then every piece goes to the 1070.
              {s.provider !== 'broker' && <strong style={{ color: T.puck }}> The stats model is not on the GPU broker, so the 5090 is not used.</strong>}
            </span>
          </span>
        </label>
        {/* 2026-10-28 (per Gilles) — queue just for the 5090: never the 1070 */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: T.text, margin: '0 0 12px', cursor: 'pointer', opacity: form.stt.sharedWhisper ? 1 : 0.5 }} data-only-5090>
          <input type="checkbox" checked={!!form.stt.only5090} disabled={!form.stt.sharedWhisper}
            onChange={e => setS('only5090', e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            <strong>5090 only</strong> — never borrow the 1070: every piece QUEUES for the 5090's shared Whisper (up to 2 hours)
            <span style={{ display: 'block', fontSize: 12, color: T.text2, marginTop: 2 }}>
              Several games re-run together interleave on the 5090, one piece at a time — they all keep moving, just slower than
              splitting across both cards. If the 5090 never frees up (training, the clinic's dictations), the pass fails with the
              reason and the retry queue picks it up.
            </span>
          </span>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={primary} disabled={saving} onClick={() => save()}><Save size={13} /> {saving ? 'Saving…' : 'Save'}</button>
          <button type="button" style={btn} disabled={sttTesting} onClick={runSttTest}><FlaskConical size={13} /> {sttTesting ? 'Testing…' : 'Test (takes a lease for a moment)'}</button>
        </div>
        {stt && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: T.s2, fontSize: 12, color: T.text }} data-stats-stt-test>
            {stt.error ? <div style={{ color: T.penalty }}>{stt.error}</div> : <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>{good(stt.ok)} {stt.ok ? 'Ready: games decode on a borrowed GPU when one is free' : 'Not ready — games decode on the CPU'}</div>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: T.text2 }}>
                <li>GPU speech model in the engine image: {stt.model && stt.faster_whisper ? 'yes' : 'no'}</li>
                <li>NVIDIA cards the engine's container sees: {stt.cuda_devices || 0}</li>
                <li>Broker: {stt.broker?.ok ? `reachable${stt.broker.broker_version ? ` (gpu-broker ${stt.broker.broker_version})` : ''}, may borrow up to ${stt.broker.max_lease_mb ?? '?'} MB` : (stt.broker?.error || 'not reachable')}</li>
                {Array.isArray(stt.broker?.gpus) && stt.broker.gpus.map(g => <li key={g.index}>GPU {g.index}: {g.availableMb ?? '?'} of {g.totalMb ?? '?'} MB free to lend{g.leasable === false ? ' (not lendable)' : ''}</li>)}
                {stt.lease && <li>Trial lease: {stt.lease.granted ? `granted on GPU ${stt.lease.gpu}, given straight back` : (stt.lease.error || 'refused')}</li>}
                {stt.shared_whisper && <li>RTX 5090 shared Whisper: {stt.shared_whisper.idle ? 'idle right now — pieces would go there' : `not used right now (${stt.shared_whisper.why || 'busy'}) — pieces go to the 1070`}</li>}
              </ul>
              {Array.isArray(stt.problems) && stt.problems.length > 0 && <div style={{ color: T.puck, marginTop: 6 }}>{stt.problems.join(' · ')}</div>}
            </>}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * 2026-10-22 (per Gilles) — the Press Box queue, live: who is processing,
 * who is waiting (for Whisper's GPU, or for their AI look), who is on hold,
 * and who is incomplete with nothing booked. Refreshes every 30 s.
 */
export function PressBoxQueueCard({ T }) {
  const [q, setQ] = useState(null);
  const [sel, setSel] = useState({});     // recordingId -> true (incomplete rows)
  const [kicking, setKicking] = useState(false);
  const [kickOut, setKickOut] = useState(null);
  const kick = async () => {
    setKicking(true); setKickOut(null);
    try { const { data } = await api.post('/admin/press-box/queue/kick', {}); setKickOut(data); }
    catch (e) { setKickOut({ audio: { error: e.response?.data?.error || 'could not run' }, video: {} }); }
    finally { setKicking(false); load(); }
  };
  const load = async () => {
    try { const { data } = await api.get('/admin/press-box/queue'); setQ(data); }
    catch (e) { setQ({ error: e.response?.data?.error || 'Could not load the queue' }); }
  };
  // 2026-10-27 (per Gilles: "nothing shows what's happening") — while a pass
  // is running or queued, look again every 10 s instead of 30.
  const busy = !!q?.rows?.some(r => ['processing', 'audio_ai_in_line', 'audio_in_line', 'waiting_for_gpu_whisper', 'queued_for_ai', 'video_ai_running', 'video_ai_queued'].includes(r.state));
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(load, busy ? 10000 : 30000); return () => clearInterval(t); }, [busy]);
  const card2 = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 16 };
  const STATE = {
    processing:                { label: 'Processing', color: T.ice },
    audio_ai_in_line:          { label: 'Audio AI in line for the GPU', color: '#ff9500' },
    audio_in_line:             { label: 'Audio re-run in line', color: '#ff9500' },
    waiting_for_gpu_whisper:   { label: 'Waiting for GPU (Whisper)', color: '#ff9500' },
    queued_for_ai:             { label: 'Queued for AI', color: T.ice },
    on_hold:                   { label: 'On hold', color: T.textDim },
    incomplete_no_look_booked: { label: 'Incomplete — nothing booked', color: '#ff6b6b' },
    video_ai_running:          { label: 'Video AI running', color: '#b069ff' },
    video_ai_queued:           { label: 'Video AI in line', color: '#ff9500' },
  };
  // with the DATE (per Gilles): "Oct 22, 2:27 PM", not just "Thu"
  const when = (iso) => iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  // 2026-10-27 (per Gilles) — dismiss incomplete rows (single or bulk):
  // nothing about the game changes, the row just leaves this list.
  const dismiss = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Remove ${ids.length} incomplete row(s) from this list? The games and their stats are untouched — a row comes back if another pass leaves it incomplete again.`)) return;
    try {
      await api.post('/admin/press-box/queue/dismiss', { recordingIds: ids });
      setSel({});
      load();
    } catch (e) { setQ(x => ({ ...x, error: e.response?.data?.error || 'Could not dismiss' })); }
  };
  const selIds = Object.keys(sel).filter(k => sel[k]);
  return (
    <div style={card2} data-pb-queue>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Press Box — the queue right now</h3>
        <button type="button" onClick={kick} disabled={kicking} data-queue-kick style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 8, cursor: 'pointer',
          fontSize: 12, background: T.ice, border: 'none', color: '#fff', fontWeight: 700 }} title="Check both lines this second (the automatic looks too) and say what each found">{kicking ? 'Running…' : 'Run the line now'}</button>
        <button type="button" onClick={load} style={{ padding: '4px 10px', borderRadius: 8, cursor: 'pointer',
          fontSize: 12, background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}>Refresh</button>
      </div>
      {/* 2026-11-04 (per Gilles: "queued but the GPU is not in use — why?") —
          the line's own state, in words, so an idle card is never a mystery */}
      {q?.line && !q.line.error && (() => {
        const L = q.line;
        const busyWith = [L.traffic?.audio ? `${L.traffic.audio} audio pass` : null, L.traffic?.video ? `${L.traffic.video} video run` : null, L.traffic?.lab ? `${L.traffic.lab} AI Lab run` : null].filter(Boolean).join(', ');
        const overdue = L.audio?.nextAt && new Date(L.audio.nextAt).getTime() < Date.now() - 120000 && !busyWith;
        const tk = (t) => t ? `${when(t.at)}${t.wait ? ` — waited: ${t.wait}` : t.error ? ` — error: ${t.error}` : t.started ? ` — started ${t.started}` : t.kind === 'looks' ? ` — ${t.due || 0} look(s) due${(t.noted || []).length ? ': ' + t.noted.join('; ') : ''}` : t.kind === 'line' ? ' — nothing in the re-run line' : ' — nothing to start'}` : 'not yet since the server started';
        return (
          <div style={{ fontSize: 12, color: overdue ? '#ff9500' : T.textDim, marginBottom: 8, whiteSpace: 'normal', lineHeight: 1.5 }} data-queue-line>
            <div><strong style={{ color: T.text }}>The card:</strong> {busyWith ? `busy with ${busyWith}` : 'free'} · policy {L.policy === '5090_only' ? '5090 only' : L.policy === '5090_first' ? '5090 first' : 'both cards'}
              {L.audioWait ? ` · audio waits: ${L.audioWait}` : ''}{L.videoWait && L.video?.queued ? ` · video waits: ${L.videoWait}` : ''}
              {L.lock ? ` · starter lock held by "${L.lock.label}" for ${L.lock.heldS} s` : ''}</div>
            <div><strong style={{ color: T.text }}>Audio line:</strong> {L.audio?.inLine || 0} re-run(s) in line, {L.audio?.dueNow || 0} look(s) due now{L.audio?.nextAt ? `, next ${when(L.audio.nextAt)}` : ''} · last check {tk(L.ticks?.audio)}</div>
            <div><strong style={{ color: T.text }}>Video line:</strong> {L.video?.queued || 0} in line · last check {tk(L.ticks?.video)}</div>
            {overdue && <div>⚠ A look is overdue while the card is free — press <em>Run the line now</em>; the answer says what stopped it.</div>}
          </div>
        );
      })()}
      {kickOut && <div style={{ fontSize: 12, color: T.text, marginBottom: 8, whiteSpace: 'normal' }} data-queue-kick-result>
        Ran the line: audio — {kickOut.audio?.error ? `error: ${kickOut.audio.error}` : `${kickOut.audio?.started || 0} started${kickOut.audio?.wait ? `, waited: ${kickOut.audio.wait}` : ''}${kickOut.audio?.noted?.length ? ` (${kickOut.audio.noted.join('; ')})` : kickOut.audio?.due === 0 ? ' (nothing due)' : ''}`};
        video — {kickOut.video?.error ? `error: ${kickOut.video.error}` : `${kickOut.video?.started || 0} started`}.
      </div>}
      {!q && <div style={{ color: T.textDim, fontSize: 13 }}>Loading…</div>}
      {q?.error && <div style={{ color: '#ff6b6b', fontSize: 13 }}>{q.error}</div>}
      {q?.rows && q.rows.length === 0 && <div style={{ color: T.textDim, fontSize: 13 }}>Quiet — nothing is processing, waiting or on hold.</div>}
      {q?.rows?.length > 0 && (
        <div>
          {selIds.length > 0 && (
            <button type="button" data-queue-dismiss-bulk onClick={() => dismiss(selIds)}
              style={{ marginBottom: 8, padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                background: 'none', border: `1px solid ${T.border}`, color: '#ff6b6b' }}>
              Remove {selIds.length} incomplete row(s) from this list
            </button>
          )}
          {/* 2026-10-27 (per Gilles) — stacked rows, no sideways scrolling */}
          {q.rows.map((r, i) => {
            const incomplete = r.state === 'incomplete_no_look_booked';
            return (
              <div key={r.recordingId || i} style={{ borderTop: `1px solid ${T.border}`, padding: '8px 0', fontSize: 12 }} data-queue-row>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {incomplete && (
                    <input type="checkbox" checked={!!sel[r.recordingId]} data-queue-select
                      onChange={(e) => setSel(x => ({ ...x, [r.recordingId]: e.target.checked }))} />
                  )}
                  <span style={{ color: STATE[r.state]?.color || T.text, fontWeight: 700 }}>{STATE[r.state]?.label || r.state}</span>
                  <span style={{ color: T.text }}>{r.team} · {r.game}{r.durationMin ? ` · ${r.durationMin} min tape` : ''}</span>
                  {incomplete && (
                    <button type="button" title="Remove this row from the list (the game and its stats are untouched)" data-queue-dismiss
                      onClick={() => dismiss([r.recordingId])}
                      style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                        background: 'none', border: `1px solid ${T.border}`, color: T.textDim }}>✕</button>
                  )}
                </div>
                <div style={{ color: T.textDim, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span>{r.model || ''}</span>
                  <span style={{ fontWeight: 600, color: r.decodedOn ? (r.decodedOn === 'CPU' ? T.puck : '#39d98a') : T.textDim }}
                    title={['processing', 'audio_ai_in_line'].includes(r.state) ? 'The LAST pass\u2019s card — the running pass\u2019s card is in the progress line below' : ''}>
                    {['processing', 'audio_ai_in_line'].includes(r.state) && r.decodedOn ? `last: ${r.decodedOn}` : (r.decodedOn || '—')}</span>
                  <span>looks {r.looks || 0}</span>
                  {r.hold ? <span>held</span> : (r.nextLookAt ? <span>next look {when(r.nextLookAt)}</span> : null)}
                  {r.lastPassAt ? <span>last pass {when(r.lastPassAt)}</span> : null}
                </div>
                <div style={{ color: ['processing', 'audio_ai_in_line', 'video_ai_running'].includes(r.state) ? T.ice : T.textDim, marginTop: 2, whiteSpace: 'normal' }}>
                  {r.state === 'video_ai_queued' ? (r.progressMsg || 'in line')
                  : r.state === 'video_ai_running' ? (() => {
                    const mins = r.passStartedAt ? Math.round((Date.now() - new Date(r.passStartedAt).getTime()) / 60000) : null;
                    const stageLabel = { starting: 'starting', sync: 'step 1/7 sound-sync', scoreboard: 'step 2/7 scoreboard',
                      goals: 'step 3/7 goal check (GPU)', zones: 'step 4/7 time in zone (GPU)', clips: 'step 5/7 cutting clips',
                      tracker: 'step 6/7 tracker (vision worker)', on_ice: 'step 7/7 who was on the ice (GPU)',
                      queued: 'clips queued', building: 'cutting clips' }[r.stage] || r.stage;
                    return [stageLabel, r.progressMsg, mins != null ? `${mins < 1 ? 'under a minute' : `${mins} min`} so far` : null]
                      .filter(Boolean).join(' · ');
                  })()
                  : r.state === 'audio_in_line' ? `waiting its turn for the 5090 (one GPU job at a time) — ${r.queued?.fresh ? 'transcribe + AI' : 'Press Box AI'}${r.queued?.by === 'bulk' ? ', from the bulk list' : ''}${r.queued?.at ? `, asked ${when(r.queued.at)}` : ''}`
                  : ['processing', 'audio_ai_in_line'].includes(r.state) ? (() => {
                    const pct = typeof r.progress === 'number' ? `${Math.round(r.progress * 100)}%` : null;
                    const mins = r.passStartedAt ? Math.round((Date.now() - new Date(r.passStartedAt).getTime()) / 60000) : null;
                    return [pct, r.progressMsg, mins != null ? `${mins < 1 ? 'under a minute' : `${mins} min`} so far` : null]
                      .filter(Boolean).join(' · ') || (r.jobId ? `job ${String(r.jobId).slice(0, 8)}` : 'running');
                  })()
                    : [r.interrupted, r.fallbackWindows ? `${r.fallbackWindows} stretch(es) fell back` : null, r.busyKind ? `busy: ${r.busyKind}` : null]
                        .filter(Boolean).join(' · ') || ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ color: T.textDim, fontSize: 11, marginTop: 6 }}>
        First come, first served; one AI retry at a time, and no retry starts while the card is busy with another pass.
      </div>
    </div>
  );
}
